import { useEffect, useState } from 'react';

type Props = {
  open: boolean;
  title: string;
  content: string;
  onClose: () => void;
  /** Fired once the exit animation has finished and the card is unmounted.
   *  Lets a caller queue the next notice without the two overlapping. */
  onClosed?: () => void;
  closeLabel: string;
};

/**
 * The notification modal — markup and enter/exit animation only.
 *
 * Shared by the ordinary feed popup and the sign-in announcement popup, which
 * differ in what they do on dismissal, not in how they look. Nothing in here
 * talks to the API: a login notice must never be marked read.
 */
export function NotificationDialog({ open, title, content, onClose, onClosed, closeLabel }: Props) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const t = setTimeout(() => setVisible(true), 16);
      return () => clearTimeout(t);
    }
    // Nothing on screen yet — no exit to animate, and no close to announce.
    if (!mounted) return;
    setVisible(false);
    const t = setTimeout(() => {
      setMounted(false);
      onClosed?.();
    }, 250);
    return () => clearTimeout(t);
    // `mounted` is read, not tracked: adding it re-runs this on the very
    // transition it schedules, cancelling the exit animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!mounted) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/50 transition-opacity duration-250"
        style={{ opacity: visible ? 1 : 0 }}
      />

      {/* Card */}
      <div
        role="dialog"
        aria-modal
        aria-label={title}
        className="fixed inset-x-0 top-1/2 z-50 mx-auto w-[calc(100%-2.5rem)] max-w-[22rem] -translate-y-1/2 overflow-hidden rounded-[1.25rem] bg-white shadow-2xl transition-all duration-250"
        style={{
          opacity: visible ? 1 : 0,
          transform: `translateY(calc(-50% + ${visible ? '0px' : '12px'}))`,
        }}
      >
        {/* Header */}
        <div className="relative px-6 pb-3 pt-6">
          <h2 className="text-center text-[1rem] font-bold leading-[1.5rem] text-black">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-[#f1f1f2]"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1 1l8 8M9 1l-8 8" stroke="#666" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="no-scrollbar overflow-y-auto px-6 pb-7" style={{ maxHeight: '60vh' }}>
          <p className="whitespace-pre-wrap text-[0.875rem] leading-[1.625rem] text-black/80">
            {content}
          </p>
        </div>
      </div>
    </>
  );
}
